import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Blob Storage Fetch Step
 *
 * Generic step: any implementation supplies its own matching via config.
 * No code changes needed for new use cases.
 *
 * Matching:
 * - blobKeyPattern: prefix to list blobs (Azure list prefix).
 * - blobKeyFilterRegex: optional regex to filter listed keys (applied after list).
 * - fileSelectionStrategy: when multiple keys match, which to pick.
 *   - most_recent / oldest: sort by Azure lastModified timestamp.
 *   - most_recent_by_name / oldest_by_name: sort by blob key lexicographically.
 *     Use when filenames contain an embedded timestamp (e.g. E844SD_R_H_20260418002556.TXT)
 *     and upstream systems may re-touch older files, corrupting lastModified-based selection.
 *
 * Placeholders (resolved at runtime in both pattern and regex):
 *   {YYYY} {YYYYMM} {YYYYMMDD} {HHmm} {HHmmss}
 *
 * Example: prefix "inbound/", regex ".*report_.*{YYYYMMDD}.*" → list inbound/, keep keys containing today's date, pick most_recent.
 */
export const blobStorageFetchStep = defineStepConfig({
  type: "blob_storage_fetch",
  name: "Fetch File from Blob Storage",
  description:
    "Generic fetch from blob storage. Pass your own list prefix and optional regex; placeholders supported.",
  category: "Data Access",

  inputSchema: z.object({}),

  outputSchema: z.object({
    fileName: z.string().describe("Name of the fetched file"),
    filePath: z.string().describe("Full blob key/path of the fetched file"),
    containerName: z.string().describe("Storage container the file resides in"),
    contentType: z.string().describe("Detected MIME type"),
    fileSize: z.number().describe("File size in bytes"),
    lastModified: z.string().describe("ISO timestamp of last modification"),
    filePaths: z
      .array(z.string())
      .describe(
        "All discovered file paths (one per folder in multi-folder mode, or single-element in single-file mode)",
      ),
    blobStorageUris: z
      .array(z.string())
      .describe(
        "Fully-qualified URIs corresponding to each entry in filePaths. " +
          "Azure: https://<account>.blob.core.windows.net/<container>/<key>. " +
          "S3: s3://<bucket>/<key>.",
      ),
    discoveredCount: z
      .number()
      .describe("Number of folders that had a matching file"),
    skippedFolders: z
      .array(z.string())
      .describe("Folder prefixes that had no matching file"),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe("Storage profile used (propagated to downstream steps)"),
  }),

  configSchema: z.object({
    containerName: z
      .string()
      .optional()
      .describe("Storage container name (defaults to system default)"),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe(
        "Which storage account to use. 'ship_and_debit' uses PROD_AZURE_STORAGE_CONNECTION_STRING. Defaults to standard storage.",
      ),
    blobKey: z.string().optional().describe("Static blob key/path to fetch"),
    blobKeyPattern: z
      .string()
      .optional()
      .describe(
        "Prefix to list blobs. Placeholders: {YYYY} {YYYYMM} {YYYYMMDD} {HHmm} {HHmmss} (resolved at runtime).",
      ),
    blobKeyFilterRegex: z
      .string()
      .optional()
      .describe(
        "Optional regex to filter listed keys. Same placeholders supported. Applied after list. Use for generic date/time matching.",
      ),
    fileSelectionStrategy: z
      .enum([
        "most_recent",
        "oldest",
        "exact",
        "most_recent_by_name",
        "oldest_by_name",
      ])
      .prefault("most_recent")
      .describe(
        "When blobKeyPattern matches multiple files, which to select. " +
          "*_by_name variants sort by blob key lexicographically — use when filenames " +
          "contain an embedded timestamp and lastModified may be stale/re-touched.",
      ),
    folderPrefixes: z
      .array(z.string())
      .optional()
      .describe(
        "List of folder prefixes to scan for multi-folder discovery. Placeholders supported: {YYYY} {YYYYMM} {YYYYMMDD} {HHmm} {HHmmss}",
      ),
    failIfNotFound: z
      .boolean()
      .prefault(true)
      .describe("Cancel workflow if no file is found"),
    failIfNoneFound: z
      .boolean()
      .prefault(false)
      .optional()
      .describe(
        "Cancel workflow if no files are found across all folders (multi-folder mode only)",
      ),
    placeholderTimezone: z
      .string()
      .optional()
      .describe(
        "IANA timezone for resolving date/time placeholders (e.g., 'America/New_York'). Defaults to America/New_York.",
      ),
    moveAfterFetch: z
      .string()
      .optional()
      .describe(
        "Move file to this path prefix after fetching (e.g., 'processed/')",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
